import React, { useState, useContext, useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import { useDrag } from 'react-use-gesture';
import styled, { css } from 'styled-components';
import { CarouselSlide } from './CarouselSlide';
import { ComponentHeader } from '../ComponentWrappers';
import { getFieldValue, defineRegionFieldFromTitle, defineAriaLabelFromTitle } from '../../utils/helpers';
import { isFieldValid } from '../../utils/validation';
import { COMPONENT_MARGIN_BOTTOM } from '../../constants';
import { media } from '../../utils/media';
import { Arrow } from '../Buttons/ArrowButtons';
import { ImageCaption } from './ImageCaption';
import { OnClickFocusWrapper } from '../UtilityWrappers';
import { BreakpointContext } from '../BreakpointProvider';
import { populateGalleryGTM } from '../../utils/gtm';
const Container = styled.div `
  display: grid;
  margin-bottom: ${COMPONENT_MARGIN_BOTTOM.DESKTOP}px;
  margin-top: ${({ hasNegativeMargin }) => hasNegativeMargin ? `calc(-30px - ${COMPONENT_MARGIN_BOTTOM.DESKTOP}px)` : ''};

  ${media.mobile} {
    margin-top: ${({ hasNegativeMargin }) => hasNegativeMargin ? `calc(-30px - ${COMPONENT_MARGIN_BOTTOM.MOBILE}px)` : ''};
    margin-bottom: ${COMPONENT_MARGIN_BOTTOM.MOBILE}px;
  }

  overflow-x: hidden;
  overflow-y: hidden;

  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
`;
const SlidesContainer = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MobileContainer = styled.div `
  ${media.smallTablet} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
const CarouselSlides = styled.div `
  position: relative;
  flex-flow: column wrap;
  overflow: hidden;

  ${media.smallTablet} {
    margin-right: 15px;
    margin-left: 15px;
  }
  ${media.mobile} {
    margin-right: 10px;
    margin-left: 10px;
  }
`;
const RectanglePaginate = styled.div `
  width: 28px;
  height: 8px;
  margin-top: 5px;
  &:not(:last-child) {
    margin-right: 5px;
  }
  background: ${({ activeSlides, theme }) => (activeSlides ? theme.primaryRed : theme.greyLightMedium)};
`;
const PaginateContainer = styled.div `
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
`;
const CaptionContainer = styled.div `
  text-align: center;
  padding-top: 20px;
  height: 84px;
  vertical-align: middle;
  max-width: 687px;
  margin: auto;
  ${media.mobileL} {
    height: 106px;
    max-width: 301px;
    padding-left: unset;
    padding-right: unset;
  }
`;
const ArrowContainer = styled.div `
  ${({ direction }) => direction === 'right' || direction === 'right-mobile'
    ? css `
          transform: rotate(270deg);
          margin-left: -10px;
        `
    : css `
          transform: rotate(90deg);
          margin-right: -10px;
        `};

  ${({ arrowMobile }) => arrowMobile
    ? css `
          margin-left: unset;
          margin-right: unset;
          @media (min-width: 993px) {
            display: none;
          }
        `
    : css `
          ${media.smallTablet} {
            display: none;
          }
        `};
`;
const V_THRESHOLD = 0.3;
const getItemImage = (item, isMobile) => {
    if (isMobile && item.fields && item.fields.mobileImage) {
        return item.fields.mobileImage;
    }
    if (item.fields && item.fields.desktopImage) {
        return item.fields.desktopImage;
    }
    return null;
};
const preloadImage = (image) => {
    if (document && document.createElement && image && image.value && image.value.src) {
        const img = document.createElement('img');
        img.src = image.value.src;
    }
};
export const Carousel = ({ fields }) => {
    if (!fields) {
        console.warn('Carousel component requires fields to be provided');
        return null;
    }
    const [currentSlide, setCurrentSlide] = useState(0);
    const [usePreviousAnimation, setUsePreviousAnimation] = useState(true);
    const { componentTitle, componentSubtitle, componentNegativeMargin, items, ariaLabel } = fields;
    const { isMobile, isResponsiveFlagsSet } = useContext(BreakpointContext);
    const rightGTM = populateGalleryGTM(true);
    const leftGTM = populateGalleryGTM(false);
    const visibleItems = items.slice(0, 6).map((item, i) => (Object.assign(Object.assign({}, item), { key: i, image: getItemImage(item, isMobile) })));
    useEffect(() => {
        if (isResponsiveFlagsSet) {
            visibleItems.map((item) => preloadImage(item.image));
        }
    }, [isResponsiveFlagsSet]);
    const leftDirection = 'left';
    const rightDirection = 'right';
    const downDirection = 'down';
    const bind = useDrag(({ last, vxvy: [vx, vy] }) => {
        if (last) {
            if (Math.abs(vx) > Math.abs(vy)) {
                if (vx < -V_THRESHOLD) {
                    nextSlide();
                }
                else if (vx > V_THRESHOLD) {
                    previousSlide();
                }
            }
        }
    });
    const defineTransitionAnimation = () => usePreviousAnimation
        ? {
            from: { position: 'relative', opacity: 1, transform: 'translate3d(-100%,0,0)' },
            enter: {
                position: 'relative',
                opacity: 1,
                transform: 'translate3d(0%,0,0)',
                willChange: 'transform',
            },
            leave: { position: 'absolute', opacity: 1, transform: 'translate3d(50%,0,0)' },
        }
        : {
            from: { position: 'relative', opacity: 1, transform: 'translate3d(100%,0,0)' },
            enter: {
                position: 'relative',
                opacity: 1,
                transform: 'translate3d(0%,0,0)',
                willChange: 'transform',
            },
            leave: { position: 'absolute', opacity: 1, transform: 'translate3d(-50%,0,0)' },
        };
    const defineCaptionTransitionAnimation = () => usePreviousAnimation
        ? {
            from: { position: 'relative', opacity: 0, transform: 'translate3d(-100%,0,0)' },
            enter: {
                position: 'relative',
                opacity: 1,
                transform: 'translate3d(0%,0,0)',
                willChange: 'transform',
            },
            leave: { position: 'absolute', opacity: 0, transform: 'translate3d(100%,0,0)', display: 'none' },
        }
        : {
            from: { position: 'relative', opacity: 0, transform: 'translate3d(100%,0,0)' },
            enter: {
                position: 'relative',
                opacity: 1,
                transform: 'translate3d(0%,0,0)',
                willChange: 'transform',
            },
            leave: { position: 'absolute', opacity: 0, transform: 'translate3d(-100%,0,0)', display: 'none' },
        };
    const nextTransitionAnimation = defineTransitionAnimation();
    const captionTransitionAnimation = defineCaptionTransitionAnimation();
    const nextTransitions = useTransition(currentSlide, (p) => p, nextTransitionAnimation);
    const captionTransitions = useTransition(currentSlide, (p) => p, captionTransitionAnimation);
    const rectangles = visibleItems.map((item) => {
        return (React.createElement(RectanglePaginate, { key: item.key, activeSlides: currentSlide === item.key, onClick: () => goToSlide(item.key) }));
    });
    function goToSlide(key) {
        setCurrentSlide(key);
    }
    function handleKeydown(key, direction) {
        if (key.key === 'Enter') {
            if (direction === 'right') {
                nextSlide();
            }
            else {
                previousSlide();
            }
        }
    }
    function previousSlide() {
        setUsePreviousAnimation(true);
        setCurrentSlide(currentSlide === 0 ? visibleItems.length - 1 : currentSlide - 1);
        leftGTM.onclick();
    }
    function nextSlide() {
        setUsePreviousAnimation(false);
        setCurrentSlide(currentSlide === visibleItems.length - 1 ? 0 : currentSlide + 1);
        rightGTM.onclick();
    }
    const margin = getFieldValue(componentNegativeMargin);
    const hasComponentHeader = !margin && isFieldValid(componentTitle);
    const carouselItems = visibleItems.map((item) => {
        return ({ style }) => (React.createElement(animated.div, Object.assign({}, bind(), { style: style, key: item.key }),
            React.createElement(CarouselSlide, { image: item.image })));
    });
    const carouselCaptionItems = visibleItems.map((item) => {
        const { imageCaption } = item.fields;
        return ({ style }) => (React.createElement(animated.div, { style: style, key: item.key },
            React.createElement(ImageCaption, { imageCaption: imageCaption })));
    });
    return (React.createElement(ComponentHeader, { title: hasComponentHeader ? componentTitle : { value: '' }, subtitle: componentSubtitle, ariaLabel: ariaLabel },
        React.createElement(Container, { hasNegativeMargin: margin, role: defineRegionFieldFromTitle(componentTitle), "aria-label": defineAriaLabelFromTitle(componentTitle, ariaLabel) },
            React.createElement(SlidesContainer, null,
                React.createElement(OnClickFocusWrapper, null,
                    React.createElement(ArrowContainer, Object.assign({ direction: leftDirection, arrowMobile: false }, leftGTM.gtmAttributes, { tabIndex: 0, onClick: previousSlide, onKeyDown: (e) => handleKeydown(e, leftDirection) }),
                        React.createElement(Arrow, { direction: downDirection, fontSize: '65' }))),
                React.createElement(CarouselSlides, null, nextTransitions.map(({ item, props, key }) => {
                    const CarouselItem = carouselItems[item];
                    return React.createElement(CarouselItem, { key: key, style: props });
                })),
                React.createElement(OnClickFocusWrapper, null,
                    React.createElement(ArrowContainer, Object.assign({ direction: rightDirection, arrowMobile: false }, rightGTM.gtmAttributes, { tabIndex: 0, onClick: nextSlide, onKeyDown: (e) => handleKeydown(e, rightDirection) }),
                        React.createElement(Arrow, { direction: downDirection, fontSize: '65' })))),
            React.createElement(CaptionContainer, null, captionTransitions.map(({ item, props, key }) => {
                const CaptionItem = carouselCaptionItems[item];
                return React.createElement(CaptionItem, { key: key, style: props });
            })),
            React.createElement(MobileContainer, null,
                React.createElement(OnClickFocusWrapper, null,
                    React.createElement(ArrowContainer, Object.assign({ direction: 'left-mobile' }, leftGTM.gtmAttributes, { arrowMobile: true, tabIndex: 0, onClick: previousSlide, onKeyDown: (e) => handleKeydown(e, leftDirection) }),
                        React.createElement(Arrow, { direction: downDirection, fontSize: '45' }))),
                React.createElement(PaginateContainer, null, rectangles),
                React.createElement(OnClickFocusWrapper, null,
                    React.createElement(ArrowContainer, Object.assign({ direction: 'right-mobile' }, rightGTM.gtmAttributes, { arrowMobile: true, tabIndex: 0, onClick: nextSlide, onKeyDown: (e) => handleKeydown(e, rightDirection) }),
                        React.createElement(Arrow, { direction: downDirection, fontSize: '45' })))))));
};
